import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { User, FileText, Save, Star, Eye, Edit } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useCandidateDraft } from "@/hooks/useCandidateDraft";

interface EvaluationViewerProps {
  candidateId: string;
  candidateName: string;
  interviewType: 'first' | 'second';
  interviewId: string;
  mode: 'view' | 'edit';
  onCompleted: () => void;
  onCancel: () => void;
}

export function EvaluationViewer({ 
  candidateId, 
  candidateName, 
  interviewType, 
  interviewId,
  mode,
  onCompleted, 
  onCancel 
}: EvaluationViewerProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [existingEvaluation, setExistingEvaluation] = useState<any>(null);
  
  // Use secure database-backed draft instead of localStorage (only in edit mode)
  const { draft, saveDraft, deleteDraft, isSaving } = useCandidateDraft(`evaluation_${interviewType}`, candidateId);
  
  const [evaluation, setEvaluation] = useState(() => {
    // Load from secure database draft if in edit mode
    if (mode === 'edit' && draft) {
      return draft;
    }
    
    // Default values
    return {
      overall_impression: '3',
      communication_skills: '3',
      technical_knowledge: '3',
      experience_relevance: '3',
      professionalism: '3',
      problem_solving: '3',
      physical_fitness: '3',
      reliability_assessment: '3',
      strengths: '',
      weaknesses: '',
      recommendation: 'proceed',
      additional_notes: ''
    };
  });

  // Save to database whenever evaluation changes (only in edit mode, debounced via hook)
  useEffect(() => {
    if (mode === 'edit') {
      const hasData = Object.values(evaluation).some(v => v !== "" && v !== "proceed" && v !== "3");
      if (hasData) {
        saveDraft(evaluation);
      }
    }
  }, [evaluation, mode]);

  const evaluationCriteria = [
    { key: 'overall_impression', label: 'Συνολική Εντύπωση' },
    { key: 'communication_skills', label: 'Επικοινωνιακές Δεξιότητες' },
    { key: 'technical_knowledge', label: 'Τεχνική Γνώση' },
    { key: 'experience_relevance', label: 'Συναφής Εμπειρία' },
    { key: 'professionalism', label: 'Επαγγελματισμός' },
    { key: 'problem_solving', label: 'Επίλυση Προβλημάτων' },
    { key: 'physical_fitness', label: 'Φυσική Κατάσταση' },
    { key: 'reliability_assessment', label: 'Αξιοπιστία' }
  ];

  const scoreLabels = {
    '1': 'Πολύ Χαμηλή',
    '2': 'Χαμηλή', 
    '3': 'Μέτρια',
    '4': 'Καλή',
    '5': 'Εξαιρετική'
  };

  const recommendationLabels = {
    'proceed': 'Συνέχεια',
    'conditional': 'Υπό Όρους',
    'reject': 'Απόρριψη'
  };

  useEffect(() => {
    fetchExistingEvaluation();
  }, [interviewId]);

  const fetchExistingEvaluation = async () => {
    try {
      // Fetch interview data
      const { data: interview, error: interviewError } = await supabase
        .from('interviews')
        .select('*')
        .eq('id', interviewId)
        .single();

      if (interviewError) throw interviewError;

      // Fetch evaluation data (get the latest one)
      const { data: evalData, error: evalError } = await supabase
        .from('candidate_evaluations')
        .select('*')
        .eq('interview_id', interviewId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (evalError) throw evalError;

      // Set existing evaluation data for update logic
      setExistingEvaluation(evalData);
      
      if (evalData) {
        // Load existing evaluation data
        setEvaluation({
          overall_impression: evalData.overall_impression?.toString() || '3',
          communication_skills: evalData.communication_skills?.toString() || '3',
          technical_knowledge: evalData.technical_knowledge?.toString() || '3',
          experience_relevance: evalData.experience_relevance?.toString() || '3',
          professionalism: evalData.professionalism?.toString() || '3',
          problem_solving: evalData.problem_solving?.toString() || '3',
          physical_fitness: evalData.physical_fitness?.toString() || '3',
          reliability_assessment: evalData.reliability_assessment?.toString() || '3',
          strengths: evalData.strengths || '',
          weaknesses: evalData.weaknesses || '',
          recommendation: interview.recommendation || 'proceed',
          additional_notes: evalData.additional_notes || interview.evaluation_notes || ''
        });
      }
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'view') return;
    
    setLoading(true);

    try {
      // Calculate average score
      const scores = evaluationCriteria.map(criteria => parseInt(evaluation[criteria.key as keyof typeof evaluation] as string));
      const averageScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;

      // Update interview with evaluation
      const { error: interviewError } = await supabase
        .from('interviews')
        .update({
          evaluation_score: Math.round(averageScore),
          evaluation_notes: evaluation.additional_notes,
          recommendation: evaluation.recommendation
        })
        .eq('id', interviewId);

      if (interviewError) throw interviewError;

      // Update existing evaluation or insert new one
      let evaluationError;
      
      if (existingEvaluation?.id) {
        // Update existing evaluation
        const { error } = await supabase
          .from('candidate_evaluations')
          .update({
            overall_impression: parseInt(evaluation.overall_impression),
            communication_skills: parseInt(evaluation.communication_skills),
            technical_knowledge: parseInt(evaluation.technical_knowledge),
            experience_relevance: parseInt(evaluation.experience_relevance),
            professionalism: parseInt(evaluation.professionalism),
            problem_solving: parseInt(evaluation.problem_solving),
            physical_fitness: parseInt(evaluation.physical_fitness),
            reliability_assessment: parseInt(evaluation.reliability_assessment),
            strengths: evaluation.strengths,
            weaknesses: evaluation.weaknesses,
            additional_notes: evaluation.additional_notes
          })
          .eq('id', existingEvaluation.id);
        evaluationError = error;
      } else {
        // Insert new evaluation
        const { error } = await supabase
          .from('candidate_evaluations')
          .insert({
            interview_id: interviewId,
            candidate_id: candidateId,
            evaluation_type: interviewType === 'first' ? 'first_interview' : 'second_interview',
            overall_impression: parseInt(evaluation.overall_impression),
            communication_skills: parseInt(evaluation.communication_skills),
            technical_knowledge: parseInt(evaluation.technical_knowledge),
            experience_relevance: parseInt(evaluation.experience_relevance),
            professionalism: parseInt(evaluation.professionalism),
            problem_solving: parseInt(evaluation.problem_solving),
            physical_fitness: parseInt(evaluation.physical_fitness),
            reliability_assessment: parseInt(evaluation.reliability_assessment),
            strengths: evaluation.strengths,
            weaknesses: evaluation.weaknesses,
            additional_notes: evaluation.additional_notes
          });
        evaluationError = error;
      }

      if (evaluationError) throw evaluationError;

      toast({
        title: "Επιτυχία",
        description: `Η αξιολόγηση της ${interviewType === 'first' ? 'πρώτης' : 'δεύτερης'} συνέντευξης ενημερώθηκε επιτυχώς`,
      });

      // Clear saved data after successful submission
      await deleteDraft();

      onCompleted();
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {mode === 'view' ? <Eye className="w-5 h-5" /> : <Edit className="w-5 h-5" />}
          {mode === 'view' ? 'Προβολή' : 'Επεξεργασία'} Αξιολόγησης {interviewType === 'first' ? 'Πρώτης' : 'Δεύτερης'} Συνέντευξης
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Υποψήφιος: <span className="font-medium">{candidateName}</span>
        </p>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Evaluation Criteria */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Star className="w-5 h-5" />
              Κριτήρια Αξιολόγησης
            </h3>
            
            <div className="grid gap-6">
              {evaluationCriteria.map((criteria) => (
                <div key={criteria.key} className="space-y-3">
                  <Label className="text-base font-medium">{criteria.label}</Label>
                  {mode === 'view' ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">
                        {evaluation[criteria.key as keyof typeof evaluation]} - {scoreLabels[evaluation[criteria.key as keyof typeof evaluation] as keyof typeof scoreLabels]}
                      </Badge>
                    </div>
                  ) : (
                    <RadioGroup
                      value={evaluation[criteria.key as keyof typeof evaluation] as string}
                      onValueChange={(value) => setEvaluation({...evaluation, [criteria.key]: value})}
                      className="flex space-x-6"
                    >
                      {Object.entries(scoreLabels).map(([score, label]) => (
                        <div key={score} className="flex items-center space-x-2">
                          <RadioGroupItem value={score} id={`${criteria.key}-${score}`} />
                          <Label htmlFor={`${criteria.key}-${score}`} className="text-sm">
                            {score} - {label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Qualitative Assessment */}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Ποιοτική Αξιολόγηση
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="strengths">Δυνατά Σημεία</Label>
                {mode === 'view' ? (
                  <div className="p-3 bg-muted rounded-md min-h-[100px]">
                    {evaluation.strengths || 'Δεν έχουν καταχωρηθεί δυνατά σημεία'}
                  </div>
                ) : (
                  <Textarea
                    id="strengths"
                    value={evaluation.strengths}
                    onChange={(e) => setEvaluation({...evaluation, strengths: e.target.value})}
                    placeholder="Περιγράψτε τα βασικά δυνατά σημεία του υποψηφίου..."
                    rows={4}
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="weaknesses">Αδυναμίες / Σημεία Βελτίωσης</Label>
                {mode === 'view' ? (
                  <div className="p-3 bg-muted rounded-md min-h-[100px]">
                    {evaluation.weaknesses || 'Δεν έχουν καταχωρηθεί αδυναμίες'}
                  </div>
                ) : (
                  <Textarea
                    id="weaknesses"
                    value={evaluation.weaknesses}
                    onChange={(e) => setEvaluation({...evaluation, weaknesses: e.target.value})}
                    placeholder="Περιγράψτε τυχόν αδυναμίες ή σημεία που χρειάζονται βελτίωση..."
                    rows={4}
                  />
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="additional_notes">Επιπρόσθετες Παρατηρήσεις</Label>
              {mode === 'view' ? (
                <div className="p-3 bg-muted rounded-md min-h-[75px]">
                  {evaluation.additional_notes || 'Δεν έχουν καταχωρηθεί επιπρόσθετες παρατηρήσεις'}
                </div>
              ) : (
                <Textarea
                  id="additional_notes"
                  value={evaluation.additional_notes}
                  onChange={(e) => setEvaluation({...evaluation, additional_notes: e.target.value})}
                  placeholder="Οποιαδήποτε επιπρόσθετη πληροφορία που θεωρείτε σημαντική..."
                  rows={3}
                />
              )}
            </div>
          </div>

          {/* Recommendation */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Σύσταση</h3>
            {mode === 'view' ? (
              <div className="flex items-center gap-2">
                <Badge 
                  className={
                    evaluation.recommendation === 'proceed' ? 'bg-green-100 text-green-800' :
                    evaluation.recommendation === 'conditional' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }
                >
                  {recommendationLabels[evaluation.recommendation as keyof typeof recommendationLabels]}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  {evaluation.recommendation === 'proceed' && interviewType === 'first' && 'Προχώρηση σε δεύτερη συνέντευξη'}
                  {evaluation.recommendation === 'proceed' && interviewType === 'second' && 'Προχώρηση σε ψυχολογικά τεστ'}
                  {evaluation.recommendation === 'conditional' && 'Συνέχεια με επιφυλάξεις'}
                  {evaluation.recommendation === 'reject' && 'Δεν συνιστάται συνέχεια'}
                </span>
              </div>
            ) : (
              <RadioGroup
                value={evaluation.recommendation}
                onValueChange={(value) => setEvaluation({...evaluation, recommendation: value})}
                className="space-y-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="proceed" id="proceed" />
                  <Label htmlFor="proceed" className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-800">Συνέχεια</Badge>
                    {interviewType === 'first' ? 'Προχώρηση σε δεύτερη συνέντευξη' : 'Προχώρηση σε ψυχολογικά τεστ'}
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="conditional" id="conditional" />
                  <Label htmlFor="conditional" className="flex items-center gap-2">
                    <Badge className="bg-yellow-100 text-yellow-800">Υπό Όρους</Badge>
                    Συνέχεια με επιφυλάξεις
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="reject" id="reject" />
                  <Label htmlFor="reject" className="flex items-center gap-2">
                    <Badge className="bg-red-100 text-red-800">Απόρριψη</Badge>
                    Δεν συνιστάται συνέχεια
                  </Label>
                </div>
              </RadioGroup>
            )}
          </div>

          <div className="flex justify-end space-x-3">
            <Button type="button" variant="outline" onClick={onCancel}>
              {mode === 'view' ? 'Κλείσιμο' : 'Ακύρωση'}
            </Button>
            {mode === 'edit' && (
              <Button type="submit" disabled={loading}>
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Αποθήκευση...' : 'Αποθήκευση Αξιολόγησης'}
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}